// @ts-nocheck
/* eslint-disable */
import { FC, ReactNode, useMemo, useEffect, useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import usePrices from 'hooks/equityhub/usePrices';
import useImpliedVolatility from 'hooks/iVol/useImpliedVolatility';
import useOptionsCalculator from 'hooks/optionsCalculator/useOptionsCalculator';
import { ProfitLossWithDomain, TableData } from 'types/riskEngine';
import {
  IVAdjustmentSettings,
  OC_SAVED_POSITIONS_CACHE_KEY,
  Position,
  PositionLeg,
  PositionShares,
} from 'types/optionsCalculator';
import { getAdjustedVolData } from 'util/optionsCalculator';
import {
  getProfitLoss,
  getSgGreeksFromDxFeedGreeks,
  getSGVolatilityData,
  getSGVolatilityDataWithOptionType,
} from 'util/riskEngine';
import { transformDividendsToRiskEngineFormat } from 'util/dividendUtils';
import { DEFAULT_MODEL_PARAMETERS } from 'config/riskEngine';
import { OptionsCalculatorDataContext } from 'hooks/optionsCalculator/useOptionsCalculatorData';
import { OptionsCalculatorActionsContext } from 'hooks/optionsCalculator/useOptionsCalculatorActions';
import { OptionsCalculatorComputedContext } from 'hooks/optionsCalculator/useOptionsCalculatorComputed';
import {
  RawGreeksDataMap,
  RawGreeksObject,
  RawStatsDataMap,
} from 'types/impliedVol';
import usePositions from './usePositions';
import useEquities from 'hooks/equityhub/useEquities';
import { EquityFieldKey, LegacyEquity, SynthOIEquity } from 'types/equityhub';
import {
  dayjs,
  getLevelsFromDataAndFields,
  isPreMarket,
  prevBusinessDayOpenMarket,
} from 'util/shared';
import { FIELD_NAME_MAPPING } from 'config';
import useRiskFreeRate from 'hooks/riskFreeRate/useRiskFreeRate';
import { RISK_FREE_RATE } from 'util/BlackScholes';
import { userSubLevelState } from 'states';
import { useRecoilValue } from 'recoil';
import { SubLevel } from 'types/user';
import { IndexSymbol, IndexSymbols } from 'types/indices';
import { GreeksResult } from 'sg-risk-engine';
import { getDividends } from 'util/dividends';

// Split contexts for better performance - components only re-render when their specific data changes
export interface OptionsCalculatorDataContextType {
  sym: string;
  currentPrice?: number;
  allVolDataCall: Map<string, TableData[]>;
  allVolDataPut: Map<string, TableData[]>;
  currentEquity?: LegacyEquity;
  currentKeyLevels?: { field: string; value: number; name: string }[];
  legs: PositionLeg[];
  shares: PositionShares[];
  ivSettings: IVAdjustmentSettings[];
  baseDate: number;
  allPrices?: Map<string, number>;
  allGreeksData?: Map<string, RawGreeksDataMap>;
  allStatsData?: Map<string, RawStatsDataMap>;
  allEquities?: Map<string, LegacyEquity | SynthOIEquity>;
  isLoading: boolean;
  error: Error | null;
}

export interface OptionsCalculatorActionsContextType {
  handleSymChange: (newSym: string) => void;
  getRiskFreeRate: (tte: number) => number;
  addLeg: (leg: PositionLeg) => void;
  removeLeg: (legId: number) => void;
  updateLeg: (legId: number, updatedFields: Partial<PositionLeg>) => void;
  updateLegsBulk: (
    legIds: number[],
    updatedFields: Partial<PositionLeg>,
  ) => void;
  clearLegs: () => void;
  removeLegsBulk: (legIds: number[]) => void;
  updateShares: (shares: PositionShares[]) => void;
  updateIVSettings: (ivSettings: Partial<IVAdjustmentSettings>) => void;
  updateBaseDate: (newBaseDate: number) => void;
  getSavedPositions: () => Promise<Position[]>;
  savePosition: (
    position: Position,
    sym: string,
    name: string,
  ) => Promise<Position>;
  editSavedPosition: (
    position: Partial<Position>,
    sym: string,
  ) => Promise<Position>;
  deleteSavedPosition: (positionId: number) => Promise<void>;
  setActivePosition: (position: Position) => void;
}

export interface OptionsCalculatorComputedContextType {
  pnlChartRef: React.RefObject<HTMLDivElement>;
  profitLoss: ProfitLossWithDomain | null;
  profitLossAtExpiration: ProfitLossWithDomain | null;
  isCalculating: boolean;
  calculationError: string | null;
  hasValidInputs: boolean;
  unadjustedIvPnl: ProfitLossWithDomain | null;
  isCalculatingUnadjustedIv: boolean;
  calculationErrorUnadjustedIv: string | null;
}

interface OptionsCalculatorProviderProps {
  children: ReactNode;
}

const PRE_MARKET_SYMS_RE = /^(?:SPX|NDX|RUT|VIX|\/ES[HMUZ]\d+:XCME)$/i;

export const OptionsCalculatorProvider: FC<OptionsCalculatorProviderProps> = ({
  children,
}) => {
  const { getCurrentGreeksData, getDailyGreeksData, getStatisticsData } =
    useImpliedVolatility({
      bypassAccessCheck: true,
    });
  const { getPrices } = usePrices({ bypassAccessCheck: true });
  const { fetchEquities } = useEquities();
  const userSubLevel = useRecoilValue(userSubLevelState);
  const pnlChartRef = useRef<HTMLDivElement | null>(null);

  const [getRiskFreeRate, setGetRiskFreeRate] = useState(
    () => (_: number) => RISK_FREE_RATE,
  );

  // Get data and actions from the URL-synced hook
  const {
    sym,
    legs,
    shares,
    ivSettings,
    baseDate,
    handleSymChange,
    addLeg,
    removeLeg,
    updateLeg,
    updateLegsBulk,
    clearLegs,
    removeLegsBulk,
    updateShares,
    updateIVSettings,
    updateBaseDate,
    setActivePosition,
  } = useOptionsCalculator();

  const { getRateFetcher } = useRiskFreeRate();
  const {
    getSavedPositions,
    savePosition,
    editSavedPosition,
    deleteSavedPosition,
  } = usePositions();

  // Create stable references for expensive operations with proper deep comparison
  const stableLegs = useMemo(() => {
    return legs;
  }, [legs]);

  const stableShares = useMemo(() => {
    return shares;
  }, [shares]);

  const stableIVSettings = useMemo(() => {
    return ivSettings;
  }, [ivSettings]);

  // Query for saved positions
  const { data: savedPositions = [] } = useQuery<Position[]>({
    queryKey: [OC_SAVED_POSITIONS_CACHE_KEY],
    queryFn: getSavedPositions,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  // Fetch prices - only when sym is valid
  const { data: allPrices, isLoading: isPricesLoading } = useQuery({
    queryKey: ['stock-prices', sym],
    queryFn: () => getPrices([]),
    enabled: !!sym && sym.length > 0,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 1000,
  });

  const {
    data: allEquities,
    isLoading: isEquitiesLoading,
    error: equitiesError,
  } = useQuery({
    queryKey: ['oc_equities'],
    queryFn: () => fetchEquities(false, undefined, true),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 1000,
  });

  const currentEquity = useMemo(() => {
    return allEquities ? (allEquities.get(sym) as LegacyEquity) : undefined;
  }, [allEquities, sym]);

  const currentPrice = useMemo(() => {
    return sym && allPrices ? allPrices.get(sym) : undefined;
  }, [allPrices, sym]);

  const currentKeyLevels = useMemo(() => {
    if (!currentEquity) {
      return undefined;
    }
    const result = getLevelsFromDataAndFields<LegacyEquity>(
      FIELD_NAME_MAPPING,
      currentEquity,
    );
    // for standard users, only return key levels for indices
    if (userSubLevel < SubLevel.PRO) {
      return IndexSymbols.has(currentEquity[EquityFieldKey.sym] as IndexSymbol)
        ? result
        : undefined; // return undefined for non-indices
    }
    return result; // return all key levels for PRO and above
  }, [currentEquity, userSubLevel]);

  useEffect(
    () => {
      const fetch = async () => {
        // TODO: When we change the logic to support historical positions and
        //  historical greeks, this date should change to be something *other*
        //  than simply "now", but to the same date for which we fetched options.
        const getter = await getRateFetcher(dayjs());
        setGetRiskFreeRate(() => getter);
      };
      fetch();
    },
    [
      /* quoteDate goes here*/
    ],
  );

  // Fetch Greeks data for all symbols (active position + saved positions)
  const allSymbols = useMemo(() => {
    const symbols = new Set<string>();

    // Only add sym if it's valid
    if (sym && sym.length > 0) {
      symbols.add(sym);
    }

    // Add symbols from saved positions
    savedPositions.forEach((position: Position) => {
      if (position.sym && position.sym.length > 0) {
        symbols.add(position.sym);
      }
    });

    return Array.from(symbols).sort(); // Sort for consistent query key
  }, [savedPositions, sym]);

  // Fetch Greeks data for all symbols with continuous polling every 30 seconds
  const {
    data: allGreeksData,
    isLoading: isGreeksLoading,
    error: greeksError,
  } = useQuery<Map<string, RawGreeksDataMap>>({
    queryKey: ['greeks', allSymbols],
    queryFn: async () => {
      if (allSymbols.length === 0) return new Map();

      const greeksPromises = allSymbols.map(async (symbol) => {
        try {
          const greeksData =
            isPreMarket() && !PRE_MARKET_SYMS_RE.test(symbol)
              ? await getDailyGreeksData(
                  prevBusinessDayOpenMarket().format('YYYY-MM-DD'),
                  symbol,
                )
              : await getCurrentGreeksData(symbol);

          return [symbol, greeksData] as [string, RawGreeksDataMap];
        } catch (error) {
          console.warn(`Failed to fetch Greeks for ${symbol}:`, error);
          return [symbol, null] as [string, RawGreeksDataMap | null];
        }
      });

      const results = await Promise.all(greeksPromises);
      const greeksMap = new Map<string, RawGreeksDataMap>();

      results.forEach(([symbol, greeksData]) => {
        if (greeksData) {
          greeksMap.set(symbol, greeksData);
        }
      });

      return greeksMap;
    },
    enabled: allSymbols.length > 0,
    staleTime: 0, // Always consider data stale to enable refetching
    gcTime: 5 * 60 * 1_000,
    refetchOnWindowFocus: false,
    refetchInterval: 30_000, // Poll every 30 seconds
    retry: 1,
    retryDelay: 1_000,
  });

  // Fetch stats data for all symbols
  const {
    data: allStatsData,
    isLoading: isStatsLoading,
    error: statsError,
  } = useQuery<Map<string, RawStatsDataMap>>({
    queryKey: ['stats', allSymbols],
    queryFn: async () => {
      if (allSymbols.length === 0) return new Map();

      const statsPromises = allSymbols.map(async (symbol) => {
        try {
          const statsData = await getStatisticsData(symbol);
          return [symbol, statsData] as [string, RawStatsDataMap];
        } catch (error) {
          console.warn(`Failed to fetch stats for ${symbol}:`, error);
          return [symbol, null] as [string, RawStatsDataMap | null];
        }
      });

      const results = await Promise.all(statsPromises);
      const statsMap = new Map<string, RawStatsDataMap>();

      results.forEach(([symbol, statsData]) => {
        if (statsData) {
          statsMap.set(symbol, statsData);
        }
      });

      return statsMap;
    },
    enabled: allSymbols.length > 0,
    staleTime: 30_000,
    gcTime: 5 * 60 * 1_000,
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 1_000,
  });

  const { data: symbolDividends = [], isLoading: isDividendsLoading } =
    useQuery({
      queryKey: ['dividends', sym],
      queryFn: async () => {
        if (!sym || sym.length === 0) return [];
        try {
          return await getDividends(sym);
        } catch (error) {
          console.warn(`Failed to fetch dividends for ${sym}:`, error);
          return [];
        }
      },
      enabled: !!sym && sym.length > 0,
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
      retryDelay: 1000,
    });

  const dividendData = useMemo(() => {
    return transformDividendsToRiskEngineFormat(symbolDividends);
  }, [symbolDividends]);

  // Calculate vol data for all symbols - memoized expensive operation
  const { allVolDataCall, allVolDataPut } = useMemo(() => {
    const volDataCallMap = new Map<string, TableData[]>();
    const volDataPutMap = new Map<string, TableData[]>();

    if (!allGreeksData || !allPrices) {
      return { allVolDataCall: volDataCallMap, allVolDataPut: volDataPutMap };
    }

    allSymbols.forEach((symbol) => {
      const greeksData = allGreeksData.get(symbol);
      const symbolPrice = allPrices.get(symbol);

      if (greeksData && symbolPrice) {
        const volDataCall = getSGVolatilityDataWithOptionType(
          greeksData,
          'C',
          symbolPrice,
          getRiskFreeRate,
        );
        const volDataPut = getSGVolatilityDataWithOptionType(
          greeksData,
          'P',
          symbolPrice,
          getRiskFreeRate,
        );

        volDataCallMap.set(symbol, volDataCall);
        volDataPutMap.set(symbol, volDataPut);
      }
    });

    return { allVolDataCall: volDataCallMap, allVolDataPut: volDataPutMap };
  }, [allGreeksData, allPrices, allSymbols, getRiskFreeRate, dividendData]);

  const allSgGreeksData = useMemo(() => {
    if (
      !allGreeksData ||
      !allVolDataCall ||
      !allVolDataPut ||
      currentPrice == null
    ) {
      return new Map<string, RawGreeksDataMap>();
    }
    const sgGreeksDataEntries = allSymbols
      .map((symbol) => {
        const greeksData = allGreeksData.get(symbol);
        const volDataCall = allVolDataCall.get(symbol);
        const volDataPut = allVolDataPut.get(symbol);
        if (greeksData && volDataCall && volDataPut) {
          return [
            symbol,
            getSgGreeksFromDxFeedGreeks(
              greeksData,
              currentPrice,
              getRiskFreeRate,
              volDataCall,
              volDataPut,
            ),
          ] as const;
        }
        return null;
      })
      .filter((entry): entry is [string, RawGreeksDataMap] => entry !== null);

    return new Map(sgGreeksDataEntries);
  }, [
    allGreeksData,
    allVolDataCall,
    allVolDataPut,
    currentPrice,
    getRiskFreeRate,
  ]);

  // Determine if we have valid inputs for calculations
  const hasValidCalculationInputs = useMemo(() => {
    return !!(
      sym &&
      sym.length > 0 &&
      allGreeksData &&
      allGreeksData.has(sym) &&
      currentPrice != null &&
      currentPrice > 0 &&
      (stableLegs.length > 0 || stableShares.length > 0)
    );
  }, [
    sym,
    allGreeksData,
    currentPrice,
    stableLegs.length,
    stableShares.length,
  ]);

  // Calculate earliest expiry for expiration calculation
  const earliestExpiry = useMemo(() => {
    if (stableLegs.length === 0) return baseDate;

    return stableLegs.reduce((earliest, leg) => {
      return leg.expiry < earliest ? leg.expiry : earliest;
    }, Infinity);
  }, [stableLegs, baseDate]);

  const allStrikes = useMemo(() => {
    if (isGreeksLoading || greeksError || !allGreeksData?.get(sym)) {
      return [];
    }
    const greeksData: RawGreeksDataMap = allGreeksData.get(sym)!;
    return Object.values(greeksData).flatMap(
      (expirationData: RawGreeksObject) =>
        Object.keys(expirationData).map(Number),
    );
  }, [allGreeksData, sym, isGreeksLoading, greeksError]);

  // INSTANT profit loss calculation - synchronous
  const { profitLoss, calculationError } = useMemo(() => {
    if (!hasValidCalculationInputs || currentPrice == null) {
      return { profitLoss: null, calculationError: null };
    }

    const adjustedVolDataCall = getAdjustedVolData(
      allVolDataCall.get(sym) || [],
      stableIVSettings,
    );
    const adjustedVolDataPut = getAdjustedVolData(
      allVolDataPut.get(sym) || [],
      stableIVSettings,
    );

    try {
      const result = getProfitLoss(
        stableLegs,
        stableShares,
        adjustedVolDataCall,
        adjustedVolDataPut,
        allStrikes,
        {
          ...DEFAULT_MODEL_PARAMETERS,
          getRiskFreeRate,
          volatility: undefined,
          baseDate,
          currentPrice: currentPrice,
        },
      );

      return { profitLoss: result, calculationError: null };
    } catch (error) {
      return {
        profitLoss: null,
        calculationError:
          error instanceof Error ? error.message : String(error),
      };
    }
  }, [
    hasValidCalculationInputs,
    currentPrice,
    stableLegs,
    stableShares,
    allVolDataCall,
    allVolDataPut,
    allStrikes,
    sym,
    stableIVSettings,
    baseDate,
  ]);

  // INSTANT profit loss at expiration calculation - synchronous
  const { profitLossAtExpiration, calculationErrorAtExpiration } =
    useMemo(() => {
      if (!hasValidCalculationInputs || currentPrice == null) {
        return {
          profitLossAtExpiration: null,
          calculationErrorAtExpiration: null,
        };
      }

      const adjustedVolDataCall = getAdjustedVolData(
        allVolDataCall.get(sym) || [],
        stableIVSettings,
      );
      const adjustedVolDataPut = getAdjustedVolData(
        allVolDataPut.get(sym) || [],
        stableIVSettings,
      );
      try {
        const result = getProfitLoss(
          stableLegs,
          stableShares,
          adjustedVolDataCall,
          adjustedVolDataPut,
          allStrikes,
          {
            ...DEFAULT_MODEL_PARAMETERS,
            volatility: undefined,
            baseDate: earliestExpiry !== Infinity ? earliestExpiry : baseDate,
            currentPrice: currentPrice,
          },
        );

        return {
          profitLossAtExpiration: result,
          calculationErrorAtExpiration: null,
        };
      } catch (error) {
        return {
          profitLossAtExpiration: null,
          calculationErrorAtExpiration:
            error instanceof Error ? error.message : String(error),
        };
      }
    }, [
      hasValidCalculationInputs,
      currentPrice,
      stableLegs,
      stableShares,
      allVolDataCall,
      allVolDataPut,
      allStrikes,
      sym,
      stableIVSettings,
      earliestExpiry,
      baseDate,
    ]);

  // INSTANT unadjusted IV profit loss calculation - synchronous
  const { unadjustedIvPnl, calculationErrorUnadjustedIv } = useMemo(() => {
    if (!hasValidCalculationInputs || currentPrice == null) {
      return { unadjustedIvPnl: null, calculationErrorUnadjustedIv: null };
    }

    const volDataCall = allVolDataCall.get(sym) || [];
    const volDataPut = allVolDataPut.get(sym) || [];
    try {
      const result = getProfitLoss(
        stableLegs,
        stableShares,
        volDataCall, // Unadjusted IV
        volDataPut, // Unadjusted IV
        allStrikes,
        {
          ...DEFAULT_MODEL_PARAMETERS,
          volatility: undefined,
          baseDate,
          currentPrice: currentPrice,
        },
      );

      return { unadjustedIvPnl: result, calculationErrorUnadjustedIv: null };
    } catch (error) {
      return {
        unadjustedIvPnl: null,
        calculationErrorUnadjustedIv:
          error instanceof Error ? error.message : String(error),
      };
    }
  }, [
    hasValidCalculationInputs,
    currentPrice,
    stableLegs,
    stableShares,
    allVolDataCall,
    allVolDataPut,
    allStrikes,
    sym,
    earliestExpiry,
    baseDate,
  ]);

  // Memoize context values to prevent unnecessary re-renders
  const dataContextValue = useMemo(
    () => ({
      sym,
      allEquities,
      allVolDataCall,
      allVolDataPut,
      currentPrice,
      currentEquity,
      currentKeyLevels,
      legs: stableLegs,
      shares: stableShares,
      ivSettings: stableIVSettings,
      baseDate,
      allPrices,
      allGreeksData: allSgGreeksData,
      allStatsData,
      isLoading:
        isGreeksLoading ||
        isStatsLoading ||
        isPricesLoading ||
        isEquitiesLoading ||
        isDividendsLoading,
      error: greeksError || statsError || equitiesError,
    }),
    [
      sym,
      currentPrice,
      allVolDataCall,
      allVolDataPut,
      currentEquity,
      currentKeyLevels,
      stableLegs,
      stableShares,
      stableIVSettings,
      baseDate,
      allPrices,
      allSgGreeksData,
      allStatsData,
      isGreeksLoading,
      isStatsLoading,
      isPricesLoading,
      allEquities,
      greeksError,
      statsError,
      isEquitiesLoading,
      equitiesError,
      isDividendsLoading,
    ],
  );

  const actionsContextValue = useMemo(
    () => ({
      handleSymChange,
      getRiskFreeRate,
      addLeg,
      removeLeg,
      updateLeg,
      updateLegsBulk,
      clearLegs,
      removeLegsBulk,
      updateShares,
      updateIVSettings,
      updateBaseDate,
      getSavedPositions,
      savePosition,
      editSavedPosition,
      deleteSavedPosition,
      setActivePosition,
    }),
    [
      getRiskFreeRate,
      handleSymChange,
      addLeg,
      removeLeg,
      updateLeg,
      updateLegsBulk,
      clearLegs,
      removeLegsBulk,
      updateShares,
      updateIVSettings,
      updateBaseDate,
      setActivePosition,
    ],
  );

  const computedContextValue = useMemo(
    () => ({
      pnlChartRef,
      profitLoss: hasValidCalculationInputs ? profitLoss : null,
      profitLossAtExpiration: hasValidCalculationInputs
        ? profitLossAtExpiration
        : null,
      isCalculating: false, // No longer async, so never calculating
      calculationError:
        calculationError || calculationErrorAtExpiration || null,
      hasValidInputs: hasValidCalculationInputs,
      unadjustedIvPnl: hasValidCalculationInputs ? unadjustedIvPnl : null,
      isCalculatingUnadjustedIv: false, // No longer async, so never calculating
      calculationErrorUnadjustedIv: hasValidCalculationInputs
        ? calculationErrorUnadjustedIv
        : null,
    }),
    [
      pnlChartRef,
      hasValidCalculationInputs,
      profitLoss,
      profitLossAtExpiration,
      unadjustedIvPnl,
      calculationError,
      calculationErrorAtExpiration,
      calculationErrorUnadjustedIv,
    ],
  );

  return (
    <OptionsCalculatorDataContext.Provider value={dataContextValue}>
      <OptionsCalculatorActionsContext.Provider value={actionsContextValue}>
        <OptionsCalculatorComputedContext.Provider value={computedContextValue}>
          {children}
        </OptionsCalculatorComputedContext.Provider>
      </OptionsCalculatorActionsContext.Provider>
    </OptionsCalculatorDataContext.Provider>
  );
};
